/**
 * The calibrate HUD (spec 2026-08-22-03 §1, §2; split by spec 2026-08-25-04 §3).
 *
 * Two operations share this panel and must never be confused:
 *
 * - **Calibration**, when the target is the frame's *primary plan*: measuring a
 *   known distance sets `mm_per_unit` and **nothing moves**. The underlay and
 *   the geometry traced over it stay glued together.
 * - **Placement**, for any other layer: the two-click anchor gesture and the
 *   measure tool re-place that layer — one pair slides it, two solve rotation
 *   and scale exactly, more are averaged.
 *
 * The copy switches on `state.mode`, so a user never reads "calibrate" while
 * the editor is about to move their drawing.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiRulerSquare, mdiTargetVariant } from '@mdi/js'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { UncalibratedBadge } from './UncalibratedBadge'
import { formatDistanceMm } from './engine/geometry'
import type { CalibrationState } from './engine/tools'
import type { SceneLayer } from './engine/scene'

interface CalibratePanelProps {
    layers: SceneLayer[]
    state: CalibrationState
    onTargetChange: (registrationUid: string) => void
    onMeasure: () => void
    /** A typed "1:N" drawing scale (spec 2026-10-16-06). */
    onStatedScale: (denominator: number) => void
    /** "Show on plan": draws the committed calibration on the stage (§3). */
    onShowCurrentChange: (on: boolean) => void
    onClear: () => void
    onApply: () => void
    onCancel: () => void
    applying: boolean
    error: string | null
}

export function CalibratePanel(props: CalibratePanelProps) {
    const { t } = useTranslation('assets')
    const { state } = props
    const calibrating = state.mode === 'calibration'
    // What the frame is calibrated to *now* (spec 2026-09-06-01 §3). The user's
    // first question in calibrate mode is whether the existing calibration is
    // plausible, and until this line the panel answered it with a bare ratio
    // and an empty stage.
    const current = calibrating ? state.current : null
    const currentMethod = current?.method ?? 'manual'
    const [scaleDraft, setScaleDraft] = useState('')
    const targetLayer = props.layers.find((l) => l.registrationUid === state.registrationUid)
    // Only a PDF page has a print size, and only the primary plan is calibrated.
    const scaleEnabled = calibrating && targetLayer?.sourceKind === 'pdf'
    const scaleValue = Number(scaleDraft)
    const scaleValid = Number.isInteger(scaleValue) && scaleValue >= 1
    const currentDetail = current?.stated_scale
        ? `1:${current.stated_scale}`
        : current?.measure
        ? formatDistanceMm(current.measure.distance_mm)
        : current?.pairs?.length
            ? t('planEditor.calibrate_current_pairs', { count: current.pairs.length })
            : null
    // Nothing is drawn for a calibration with no audit trail, so the toggle
    // would be a switch with nothing on the other end.
    const drawable = Boolean(
        (current?.measure && (currentMethod === 'measure' || currentMethod === 'auto'))
        || (currentMethod === 'anchor_pairs' && current?.pairs?.length),
    )
    const hint = state.measuring
        ? t(calibrating ? 'planEditor.calibrate_hint_measure_scale' : 'planEditor.calibrate_hint_measure')
        : state.awaitingTarget
            ? t('planEditor.calibrate_hint_target')
            : t(calibrating ? 'planEditor.calibrate_hint_plan' : 'planEditor.calibrate_hint_source')

    return (
        <div
            className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-3 py-2 text-xs"
            data-testid="plan-editor-calibrate"
            role="region"
            aria-label={t('planEditor.calibrate')}
        >
            <AppIcon tone="muted" path={mdiTargetVariant} size="sm" />
            <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('planEditor.calibrate_target')}</span>
                <select
                    className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                    value={state.registrationUid ?? ''}
                    aria-label={t('planEditor.calibrate_target')}
                    data-testid="plan-editor-calibrate-target"
                    onChange={(e) => props.onTargetChange(e.target.value)}
                >
                    {!props.layers.length && <option value="">{t('planEditor.calibrate_no_layer')}</option>}
                    {props.layers.map((layer) => (
                        <option key={layer.registrationUid} value={layer.registrationUid}>
                            {layer.label} ({t(`planEditor.layer_kinds.${layer.kind}`)})
                        </option>
                    ))}
                </select>
            </label>

            <span className="mx-1 h-5 w-px bg-border" aria-hidden />

            <div className="flex items-center gap-1" data-testid="plan-editor-pairs">
                <span className="text-muted-foreground">{t('planEditor.calibrate_pairs')}</span>
                {state.pairs.length === 0 && (
                    <span className="text-muted-foreground" data-testid="plan-editor-pairs-empty">—</span>
                )}
                {state.pairs.map((pair, i) => (
                    <span
                        key={`${pair.source.x}:${pair.source.y}:${i}`}
                        className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground"
                        data-testid={`plan-editor-pair-${i + 1}`}
                        title={`${Math.round(pair.source.x)},${Math.round(pair.source.y)} → ${Math.round(pair.target.x)},${Math.round(pair.target.y)}`}
                    >
                        {i + 1}
                    </span>
                ))}
            </div>

            <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${calibrating ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}
                data-testid="plan-editor-calibrate-mode"
            >
                {t(calibrating ? 'planEditor.calibrate_mode_calibration' : 'planEditor.calibrate_mode_placement')}
            </span>

            <span className="text-muted-foreground" data-testid="plan-editor-calibrate-hint">{hint}</span>

            {calibrating && state.mmPerUnit !== null && (
                <span className="text-muted-foreground" data-testid="plan-editor-calibrate-scale">
                    {t('planEditor.calibrate_scale', { value: state.mmPerUnit.toFixed(4) })}
                </span>
            )}
            {calibrating && state.mmPerUnit === null && <UncalibratedBadge />}

            {current && (
                <span
                    className="flex items-center gap-2 text-muted-foreground"
                    data-testid="plan-editor-calibrate-current"
                >
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                        {t(`planEditor.calibrate_methods.${currentMethod}`)}
                    </span>
                    {currentDetail && <span data-testid="plan-editor-calibrate-current-detail">{currentDetail}</span>}
                    {drawable && (
                        <label className="flex items-center gap-1" title={t('planEditor.calibrate_show_current_hint')}>
                            <Checkbox
                                checked={state.showCurrent}
                                data-testid="plan-editor-calibrate-show-current"
                                onCheckedChange={(v) => props.onShowCurrentChange(v === true)}
                            />
                            <span>{t('planEditor.calibrate_show_current')}</span>
                        </label>
                    )}
                </span>
            )}

            {calibrating && (
                <label
                    className="flex items-center gap-1"
                    title={scaleEnabled ? undefined : t('planEditor.calibrate_scale_ratio_disabled')}
                >
                    <span className="text-muted-foreground">{t('planEditor.calibrate_scale_ratio')}</span>
                    <Input
                        className="h-7 w-16 text-xs"
                        inputMode="numeric"
                        placeholder="100"
                        value={scaleDraft}
                        disabled={!scaleEnabled}
                        aria-label={t('planEditor.calibrate_scale_ratio')}
                        data-testid="plan-editor-calibrate-scale-input"
                        onChange={(e) => setScaleDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && scaleValid) {
                                e.stopPropagation()
                                props.onStatedScale(scaleValue)
                            }
                        }}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        disabled={!scaleEnabled || !scaleValid}
                        data-testid="plan-editor-calibrate-scale-set"
                        onClick={() => props.onStatedScale(scaleValue)}
                    >
                        {t('planEditor.calibrate_scale_set')}
                    </Button>
                </label>
            )}
            {calibrating && !scaleEnabled && (
                <span className="text-[11px] text-muted-foreground" data-testid="plan-editor-calibrate-scale-hint">
                    {t('planEditor.calibrate_scale_ratio_disabled')}
                </span>
            )}

            <div className="ml-auto flex items-center gap-1">
                <Button
                    variant={state.measuring ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-[11px]"
                    aria-pressed={state.measuring}
                    aria-label={t('planEditor.measure')}
                    title={t('planEditor.measure_hint')}
                    data-testid="plan-editor-measure"
                    disabled={!state.registrationUid}
                    onClick={props.onMeasure}
                >
                    <AppIcon path={mdiRulerSquare} size="xs" className="md:mr-1" />
                    <span className="hidden md:inline">{t('planEditor.measure')}</span>
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px]"
                    data-testid="plan-editor-calibrate-clear"
                    disabled={!state.pairs.length && !state.awaitingTarget}
                    onClick={props.onClear}
                >
                    {t('planEditor.calibrate_clear')}
                </Button>
                <Button
                    variant="default"
                    size="sm"
                    className="h-7 text-[11px]"
                    aria-label={t('planEditor.calibrate_apply')}
                    title={`${t('planEditor.calibrate_apply')} (Enter)`}
                    data-testid="plan-editor-calibrate-apply"
                    disabled={!state.dirty || props.applying}
                    onClick={props.onApply}
                >
                    <AppIcon path={mdiCheck} size="xs" className="md:mr-1" />
                    <span className="hidden md:inline">
                        {props.applying ? t('planEditor.saving') : t('planEditor.calibrate_apply')}
                    </span>
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px]"
                    aria-label={t('planEditor.calibrate_cancel')}
                    title={`${t('planEditor.calibrate_cancel')} (Esc)`}
                    data-testid="plan-editor-calibrate-cancel"
                    onClick={props.onCancel}
                >
                    <AppIcon path={mdiClose} size="xs" />
                </Button>
            </div>

            <p className="w-full text-[11px] text-muted-foreground" data-testid="plan-editor-calibrate-explainer">
                {t(calibrating ? 'planEditor.calibrate_explainer_calibration' : 'planEditor.calibrate_explainer_placement')}
            </p>

            {props.error && (
                <p className="w-full text-destructive" data-testid="plan-editor-calibrate-error">{props.error}</p>
            )}
        </div>
    )
}

export default CalibratePanel
